import { computed } from '@preact/signals-react';
import { t } from 'i18next';
import { useState } from 'react';
import { ProjectExportOptions } from '../../options/ProjectExportOptions';
import { CsvExportManager } from '../../services/CsvExportManager';
import { PrintExportManager } from '../../services/PrintExportManager';
import { ExportFormat, ProjectExportState } from '../states/ProjectExportState';
import { ExportDrawerChips, ExportsDrawer } from './components/ExportsDrawer';
import { CsvSelector } from './components/selectors/CsvSelector';
import { PrintSelector } from './components/selectors/PrintSelector';

type Props = {
  onError: (e: unknown) => void;
};

export const ProjectExportDrawer = ({ onError }: Props) => {
  const state = ProjectExportState.getInstance();
  const [printManager] = useState(new PrintExportManager());
  const [csvManager] = useState(new CsvExportManager());

  const [pending, setPending] = useState(false);

  const chips = computed<ExportDrawerChips[]>(() => [
    { title: t('project'), chips: [state.project.value?.getName() ?? ''] },
    { title: t('defaultProfile'), chips: [state.profile.value?.getLabel() ?? t('noProfileInReport')] },
  ]);

  const onExportClick = async () => {
    const { project, withExceptions, format } = state.peek();

    if (!project) return;

    const exportOptions: ProjectExportOptions = {
      withExceptions,
    };

    try {
      setPending(true);

      const manager = format === ExportFormat.CSV ? csvManager : printManager;
      await manager.exportProjectDashboard(project.getId(), project.getName(), exportOptions);

      setPending(false);
    } catch (e: unknown) {
      setPending(false);
      onError(e);
    }
  };

  return (
    <ExportsDrawer
      title={t('exportTitles.project')}
      disabled={!state.project.value || state.project.value.getIssueCount() === 0 || pending}
      chips={chips.value}
      selectors={[
        <PrintSelector
          key="print"
          description={t('exportFormats.print.project')}
          selected={state.format.value === ExportFormat.PRINT}
          loading={pending}
          onSelected={() => state.setFormat(ExportFormat.PRINT)}
        />,
        <CsvSelector
          key="csv"
          description={t('exportFormats.csv.project')}
          selected={state.format.value === ExportFormat.CSV}
          loading={pending}
          onSelected={() => state.setFormat(ExportFormat.CSV)}
        />,
      ]}
      actionButtonProps={{
        children: state.format.value === ExportFormat.PRINT ? t('print') : t('download'),
        onClick: onExportClick,
        disabled: !state.project.value || pending,
        loading: pending,
      }}
    />
  );
};
